import { Fragment, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Dialog, Transition, Tab, Listbox } from '@headlessui/react';
import {
  ArrowLeftIcon,
  PlusIcon,
  TrashIcon,
  LinkIcon,
  ArrowPathIcon,
  ChevronUpDownIcon,
  CheckIcon,
  ChevronDownIcon,
  ShieldCheckIcon,
  ExclamationTriangleIcon,
} from '@heroicons/react/24/outline';
import toast from 'react-hot-toast';
import clsx from 'clsx';
import { campaignApi, channelRequirementApi } from '../lib/api';
import { useTeamId } from '../hooks/useTeamId';
import { StatusBadge } from '../components/ui';
import type { BadgeVariant } from '../components/ui';

// ─── Types ────────────────────────────────────────────────────────────────────

type CampaignStatus = 'PLANNING' | 'IN_REVIEW' | 'ACTIVE' | 'PAUSED' | 'COMPLETED' | 'CANCELLED';

type ContentChannelType =
  | 'PRINT'
  | 'WEB'
  | 'SOCIAL'
  | 'EMAIL'
  | 'RADIO'
  | 'TV'
  | 'DIRECT_MAIL'
  | 'IN_BRANCH'
  | 'MOBILE';

type PieceComplianceStatus = 'CURRENT' | 'NON_COMPLIANT' | 'UNDER_REVIEW' | 'pending_review' | null;

interface Campaign {
  id: string;
  name: string;
  description?: string;
  status: CampaignStatus;
  channels: ContentChannelType[];
  startDate: string;
  endDate: string;
  piecesCount: number;
  complianceStatus: string | null;
  targetProductTypes?: string[];
}

interface ContentPiece {
  id: string;
  channel: ContentChannelType;
  title: string;
  description?: string;
  contentUrl?: string;
  complianceStatus: PieceComplianceStatus;
  linkedReportId?: string | null;
  reviewDeadline?: string | null;
}

interface ChannelRequirement {
  id: string;
  channel: ContentChannelType;
  requirement: string;
  description?: string;
  required: boolean;
  regulationUrl?: string | null;
}

interface ChannelSummary {
  channel: ContentChannelType;
  totalPieces: number;
  compliant: number;
  pending: number;
  issues: number;
}

interface ComplianceSummary {
  overallStatus: 'compliant' | 'pending_review' | 'has_issues';
  channels: ChannelSummary[];
  unresolvedIssues: {
    pieceId: string;
    pieceTitle: string;
    channel: ContentChannelType;
    status: PieceComplianceStatus;
  }[];
}

interface AddPieceForm {
  channel: ContentChannelType;
  title: string;
  description: string;
  contentUrl: string;
  reviewDeadline: string;
}

// ─── Constants ────────────────────────────────────────────────────────────────

const STATUS_VARIANT: Record<CampaignStatus, BadgeVariant> = {
  PLANNING: 'draft',
  IN_REVIEW: 'info',
  ACTIVE: 'success',
  PAUSED: 'warning',
  COMPLETED: 'neutral',
  CANCELLED: 'danger',
};

const STATUS_LABEL: Record<CampaignStatus, string> = {
  PLANNING: 'Planning',
  IN_REVIEW: 'In Review',
  ACTIVE: 'Active',
  PAUSED: 'Paused',
  COMPLETED: 'Completed',
  CANCELLED: 'Cancelled',
};

const CHANNEL_DISPLAY: Record<ContentChannelType, string> = {
  PRINT: 'Print',
  WEB: 'Web',
  SOCIAL: 'Social',
  EMAIL: 'Email',
  RADIO: 'Radio',
  TV: 'TV',
  DIRECT_MAIL: 'Direct Mail',
  IN_BRANCH: 'In-Branch',
  MOBILE: 'Mobile',
};

const ALL_CHANNELS: ContentChannelType[] = [
  'PRINT', 'WEB', 'SOCIAL', 'EMAIL', 'RADIO', 'TV', 'DIRECT_MAIL', 'IN_BRANCH', 'MOBILE',
];

const PIECE_COMPLIANCE_VARIANT: Record<string, BadgeVariant> = {
  CURRENT: 'success',
  NON_COMPLIANT: 'danger',
  UNDER_REVIEW: 'warning',
  pending_review: 'warning',
};

const PIECE_COMPLIANCE_LABEL: Record<string, string> = {
  CURRENT: 'No Issues',
  NON_COMPLIANT: 'Flagged for Review',
  UNDER_REVIEW: 'Under Review',
  pending_review: 'Pending Review',
};

const STATUS_TRANSITIONS: Record<CampaignStatus, { label: string; status: CampaignStatus; variant: string }[]> = {
  PLANNING: [
    { label: 'Submit for Review', status: 'IN_REVIEW', variant: 'bg-teal-600 hover:bg-teal-500 text-white' },
  ],
  IN_REVIEW: [
    { label: 'Activate', status: 'ACTIVE', variant: 'bg-green-600 hover:bg-green-500 text-white' },
    { label: 'Back to Planning', status: 'PLANNING', variant: 'border border-gray-300 text-gray-700 hover:bg-gray-50' },
  ],
  ACTIVE: [
    { label: 'Pause', status: 'PAUSED', variant: 'bg-amber-500 hover:bg-amber-400 text-white' },
    { label: 'Complete', status: 'COMPLETED', variant: 'bg-gray-600 hover:bg-gray-500 text-white' },
  ],
  PAUSED: [
    { label: 'Resume', status: 'ACTIVE', variant: 'bg-green-600 hover:bg-green-500 text-white' },
    { label: 'Cancel', status: 'CANCELLED', variant: 'bg-red-600 hover:bg-red-500 text-white' },
  ],
  COMPLETED: [],
  CANCELLED: [],
};

const DEFAULT_PIECE_FORM: AddPieceForm = {
  channel: 'WEB',
  title: '',
  description: '',
  contentUrl: '',
  reviewDeadline: '',
};

// ─── Component ────────────────────────────────────────────────────────────────

export default function CampaignDetailPage() {
  const { id } = useParams<{ id: string }>();
  const queryClient = useQueryClient();
  const { teamId } = useTeamId();

  const [showAddPieceModal, setShowAddPieceModal] = useState(false);
  const [showLinkReportModal, setShowLinkReportModal] = useState(false);
  const [activePieceId, setActivePieceId] = useState<string | null>(null);
  const [linkReportId, setLinkReportId] = useState('');
  const [pieceForm, setPieceForm] = useState<AddPieceForm>(DEFAULT_PIECE_FORM);
  const [expandedChannels, setExpandedChannels] = useState<Set<string>>(new Set());

  // ── Queries ──────────────────────────────────────────────────────────────

  const { data: campaignData, isLoading: campaignLoading, isError: campaignError } = useQuery({
    queryKey: ['campaign', teamId, id],
    queryFn: () => campaignApi.get(teamId!, id!),
    enabled: !!teamId && !!id,
    retry: 1,
  });
  const campaign: Campaign | undefined = campaignData?.data;

  const { data: summaryData, refetch: refetchSummary } = useQuery({
    queryKey: ['campaign', teamId, id, 'compliance-summary'],
    queryFn: () => campaignApi.complianceSummary(teamId!, id!),
    enabled: !!teamId && !!id,
  });
  const summary: ComplianceSummary | undefined = summaryData?.data;

  const { data: requirementsData } = useQuery({
    queryKey: ['channel-requirements', teamId],
    queryFn: () => channelRequirementApi.list(teamId!),
    enabled: !!teamId,
  });
  const requirements: ChannelRequirement[] = requirementsData?.data?.requirements || [];

  // Derive pieces from the campaign data (nested in response)
  const pieces: ContentPiece[] = (campaignData?.data as any)?.pieces || [];

  // ── Mutations ────────────────────────────────────────────────────────────

  const statusMutation = useMutation({
    mutationFn: (newStatus: CampaignStatus) =>
      campaignApi.updateStatus(teamId!, id!, { status: newStatus }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['campaign', teamId, id] });
      queryClient.invalidateQueries({ queryKey: ['campaigns'] });
      toast.success('Campaign status updated');
    },
    onError: (err: any) => {
      toast.error(err.response?.data?.error || 'Failed to update status');
    },
  });

  const addPieceMutation = useMutation({
    mutationFn: (data: AddPieceForm) => campaignApi.addPiece(teamId!, id!, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['campaign', teamId, id] });
      toast.success('Content piece added');
      setShowAddPieceModal(false);
      setPieceForm(DEFAULT_PIECE_FORM);
    },
    onError: (err: any) => {
      toast.error(err.response?.data?.error || 'Failed to add piece');
    },
  });

  const deletePieceMutation = useMutation({
    mutationFn: (pieceId: string) => campaignApi.deletePiece(teamId!, id!, pieceId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['campaign', teamId, id] });
      toast.success('Content piece deleted');
    },
    onError: (err: any) => {
      toast.error(err.response?.data?.error || 'Failed to delete piece');
    },
  });

  const linkReportMutation = useMutation({
    mutationFn: ({ pieceId, reportId }: { pieceId: string; reportId: string }) =>
      campaignApi.linkReport(teamId!, id!, pieceId, { reportId }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['campaign', teamId, id] });
      toast.success('Report linked successfully');
      setShowLinkReportModal(false);
      setActivePieceId(null);
      setLinkReportId('');
    },
    onError: (err: any) => {
      toast.error(err.response?.data?.error || 'Failed to link report');
    },
  });

  const seedMutation = useMutation({
    mutationFn: () => channelRequirementApi.seed(teamId!),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['channel-requirements'] });
      toast.success('Channel requirements seeded');
    },
    onError: (err: any) => {
      toast.error(err.response?.data?.error || 'Failed to seed requirements');
    },
  });

  // ── Handlers ─────────────────────────────────────────────────────────────

  const handleAddPiece = () => {
    if (!pieceForm.title.trim()) {
      toast.error('Title is required');
      return;
    }
    addPieceMutation.mutate(pieceForm);
  };

  const handleDeletePiece = (pieceId: string, title: string) => {
    if (window.confirm(`Delete content piece "${title}"?`)) {
      deletePieceMutation.mutate(pieceId);
    }
  };

  const openLinkReportModal = (pieceId: string) => {
    setActivePieceId(pieceId);
    setLinkReportId('');
    setShowLinkReportModal(true);
  };

  const handleLinkReport = () => {
    if (!activePieceId || !linkReportId.trim()) {
      toast.error('Report ID is required');
      return;
    }
    linkReportMutation.mutate({ pieceId: activePieceId, reportId: linkReportId.trim() });
  };

  const toggleChannelExpansion = (channel: string) => {
    setExpandedChannels((prev) => {
      const next = new Set(prev);
      if (next.has(channel)) {
        next.delete(channel);
      } else {
        next.add(channel);
      }
      return next;
    });
  };

  const handleRefreshSummary = () => {
    refetchSummary();
    toast.success('Compliance summary refreshed');
  };

  // ── Group requirements by channel ────────────────────────────────────────

  const requirementsByChannel = requirements.reduce<Record<string, ChannelRequirement[]>>(
    (acc, req) => {
      if (!acc[req.channel]) acc[req.channel] = [];
      acc[req.channel].push(req);
      return acc;
    },
    {},
  );

  // ── Loading / Not Found ──────────────────────────────────────────────────

  if (campaignLoading) {
    return (
      <div className="flex items-center justify-center p-12">
        <div className="h-8 w-8 animate-spin rounded-full border-4 border-teal-600 border-t-transparent" />
      </div>
    );
  }

  if (campaignError || !campaign) {
    return (
      <div className="text-center p-12">
        <p className="text-gray-500">Campaign not found.</p>
        <Link to="/campaigns" className="mt-2 inline-block text-sm text-teal-600 hover:underline">
          Back to campaigns
        </Link>
      </div>
    );
  }

  const transitions = STATUS_TRANSITIONS[campaign.status] || [];

  // ── Render ───────────────────────────────────────────────────────────────

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <Link
          to="/campaigns"
          className="inline-flex items-center gap-1 text-sm text-gray-500 hover:text-teal-600 transition-colors"
        >
          <ArrowLeftIcon className="h-4 w-4" />
          Back to Campaigns
        </Link>

        <div className="mt-3 flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
          <div className="flex items-center gap-3">
            <h1 className="text-2xl font-semibold text-navy-900">{campaign.name}</h1>
            <StatusBadge variant={STATUS_VARIANT[campaign.status]}>
              {STATUS_LABEL[campaign.status]}
            </StatusBadge>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            {transitions.map((t) => (
              <button
                key={t.status}
                onClick={() => statusMutation.mutate(t.status)}
                disabled={statusMutation.isPending}
                className={clsx(
                  'rounded-lg px-3 py-1.5 text-sm font-medium transition-colors disabled:opacity-50',
                  t.variant,
                )}
              >
                {t.label}
              </button>
            ))}
          </div>
        </div>

        <div className="mt-2 flex flex-wrap items-center gap-3 text-sm text-gray-500">
          <span>
            {new Date(campaign.startDate).toLocaleDateString()} &ndash;{' '}
            {new Date(campaign.endDate).toLocaleDateString()}
          </span>
          <span className="text-gray-300">|</span>
          <div className="flex flex-wrap gap-1">
            {campaign.channels.map((ch) => (
              <StatusBadge key={ch} variant="neutral">
                {CHANNEL_DISPLAY[ch]}
              </StatusBadge>
            ))}
          </div>
        </div>

        {campaign.description && (
          <p className="mt-2 text-sm text-gray-600">{campaign.description}</p>
        )}
      </div>

      {/* Tabs */}
      <Tab.Group>
        <Tab.List className="flex space-x-1 border-b border-gray-200">
          {['Content Pieces', 'Channel Requirements', 'Compliance Summary'].map((tab) => (
            <Tab
              key={tab}
              className={({ selected }) =>
                clsx(
                  'whitespace-nowrap border-b-2 px-4 py-2.5 text-sm font-medium transition-colors focus:outline-none',
                  selected
                    ? 'border-teal-600 text-teal-600'
                    : 'border-transparent text-gray-500 hover:border-gray-300 hover:text-gray-700',
                )
              }
            >
              {tab}
            </Tab>
          ))}
        </Tab.List>

        <Tab.Panels className="mt-4">
          {/* ── Tab 1: Content Pieces ───────────────────────────────────────── */}
          <Tab.Panel>
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <h2 className="text-lg font-semibold text-navy-900">
                  Content Pieces ({pieces.length})
                </h2>
                <button
                  onClick={() => setShowAddPieceModal(true)}
                  className="inline-flex items-center gap-2 rounded-lg bg-teal-600 px-3 py-1.5 text-sm font-medium text-white hover:bg-teal-500 transition-colors"
                >
                  <PlusIcon className="h-4 w-4" />
                  Add Piece
                </button>
              </div>

              {pieces.length === 0 ? (
                <div className="rounded-lg border border-gray-100 bg-white p-12 text-center shadow-sm">
                  <p className="text-sm text-gray-500">No content pieces yet.</p>
                  <button
                    onClick={() => setShowAddPieceModal(true)}
                    className="mt-3 inline-flex items-center gap-2 rounded-lg bg-teal-600 px-3 py-1.5 text-sm font-medium text-white hover:bg-teal-500"
                  >
                    <PlusIcon className="h-4 w-4" />
                    Add Piece
                  </button>
                </div>
              ) : (
                <div className="overflow-hidden rounded-lg border border-gray-100 bg-white shadow-sm">
                  <div className="overflow-x-auto">
                    <table className="w-full">
                      <thead>
                        <tr className="border-b border-gray-200 bg-gray-50">
                          <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider text-gray-600">
                            Channel
                          </th>
                          <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider text-gray-600">
                            Title
                          </th>
                          <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider text-gray-600">
                            Compliance
                          </th>
                          <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider text-gray-600">
                            Linked Report
                          </th>
                          <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider text-gray-600">
                            Review Deadline
                          </th>
                          <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider text-gray-600">
                            Actions
                          </th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-gray-100">
                        {pieces.map((piece) => (
                          <tr key={piece.id} className="transition-colors hover:bg-gray-50">
                            <td className="px-4 py-3 text-sm">
                              <StatusBadge variant="neutral">
                                {CHANNEL_DISPLAY[piece.channel]}
                              </StatusBadge>
                            </td>
                            <td className="px-4 py-3 text-sm font-medium text-gray-900">
                              {piece.title}
                            </td>
                            <td className="px-4 py-3 text-sm">
                              {piece.complianceStatus ? (
                                <StatusBadge
                                  variant={PIECE_COMPLIANCE_VARIANT[piece.complianceStatus] || 'neutral'}
                                >
                                  {PIECE_COMPLIANCE_LABEL[piece.complianceStatus] || piece.complianceStatus}
                                </StatusBadge>
                              ) : (
                                <StatusBadge variant="neutral">Not Reviewed</StatusBadge>
                              )}
                            </td>
                            <td className="px-4 py-3 text-sm">
                              {piece.linkedReportId ? (
                                <Link
                                  to={`/reports/${piece.linkedReportId}`}
                                  className="text-teal-600 hover:text-teal-700 hover:underline"
                                >
                                  {piece.linkedReportId.slice(0, 8)}...
                                </Link>
                              ) : (
                                <span className="text-xs text-gray-400">Not linked</span>
                              )}
                            </td>
                            <td className="whitespace-nowrap px-4 py-3 text-sm text-gray-500">
                              {piece.reviewDeadline
                                ? new Date(piece.reviewDeadline).toLocaleDateString()
                                : '--'}
                            </td>
                            <td className="px-4 py-3 text-sm">
                              <div className="flex items-center gap-2">
                                <button
                                  onClick={() => openLinkReportModal(piece.id)}
                                  className="inline-flex items-center gap-1 rounded-md px-2 py-1.5 text-xs font-medium text-teal-600 hover:bg-teal-50 transition-colors"
                                  title="Link Report"
                                >
                                  <LinkIcon className="h-3.5 w-3.5" />
                                  Link Report
                                </button>
                                <button
                                  onClick={() => handleDeletePiece(piece.id, piece.title)}
                                  className="inline-flex items-center gap-1 rounded-md px-2 py-1.5 text-xs font-medium text-red-600 hover:bg-red-50 transition-colors"
                                  title="Delete"
                                >
                                  <TrashIcon className="h-3.5 w-3.5" />
                                  Delete
                                </button>
                              </div>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}
            </div>
          </Tab.Panel>

          {/* ── Tab 2: Channel Requirements ─────────────────────────────────── */}
          <Tab.Panel>
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <h2 className="text-lg font-semibold text-navy-900">Channel Requirements</h2>
                <button
                  onClick={() => seedMutation.mutate()}
                  disabled={seedMutation.isPending}
                  className="inline-flex items-center gap-2 rounded-lg border border-teal-600 px-3 py-1.5 text-sm font-medium text-teal-600 hover:bg-teal-50 transition-colors disabled:opacity-50"
                >
                  <ArrowPathIcon className={clsx('h-4 w-4', seedMutation.isPending && 'animate-spin')} />
                  Seed Requirements
                </button>
              </div>

              {Object.keys(requirementsByChannel).length === 0 ? (
                <div className="rounded-lg border border-gray-100 bg-white p-12 text-center shadow-sm">
                  <p className="text-sm text-gray-500">
                    No channel requirements configured. Click "Seed Requirements" to populate defaults.
                  </p>
                </div>
              ) : (
                <div className="space-y-3">
                  {Object.entries(requirementsByChannel).map(([channel, reqs]) => (
                    <div
                      key={channel}
                      className="overflow-hidden rounded-lg border border-gray-100 bg-white shadow-sm"
                    >
                      <button
                        onClick={() => toggleChannelExpansion(channel)}
                        className="flex w-full items-center justify-between px-4 py-3 text-left hover:bg-gray-50 transition-colors"
                      >
                        <div className="flex items-center gap-2">
                          <StatusBadge variant="neutral">
                            {CHANNEL_DISPLAY[channel as ContentChannelType] || channel}
                          </StatusBadge>
                          <span className="text-sm text-gray-500">
                            {reqs.length} requirement{reqs.length !== 1 ? 's' : ''}
                          </span>
                        </div>
                        <ChevronDownIcon
                          className={clsx(
                            'h-4 w-4 text-gray-400 transition-transform',
                            expandedChannels.has(channel) && 'rotate-180',
                          )}
                        />
                      </button>

                      {expandedChannels.has(channel) && (
                        <div className="border-t border-gray-100">
                          <ul className="divide-y divide-gray-50">
                            {reqs.map((req) => (
                              <li key={req.id} className="px-4 py-3">
                                <div className="flex items-start justify-between gap-3">
                                  <div className="min-w-0">
                                    <p className="text-sm font-medium text-gray-900">
                                      {req.requirement}
                                    </p>
                                    {req.description && (
                                      <p className="mt-0.5 text-xs text-gray-500">
                                        {req.description}
                                      </p>
                                    )}
                                    {req.regulationUrl && (
                                      <a
                                        href={req.regulationUrl}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className="mt-1 inline-block text-xs text-teal-600 hover:underline"
                                      >
                                        View Regulation
                                      </a>
                                    )}
                                  </div>
                                  <StatusBadge variant={req.required ? 'danger' : 'neutral'}>
                                    {req.required ? 'Required' : 'Optional'}
                                  </StatusBadge>
                                </div>
                              </li>
                            ))}
                          </ul>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
          </Tab.Panel>

          {/* ── Tab 3: Compliance Summary ───────────────────────────────────── */}
          <Tab.Panel>
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <h2 className="text-lg font-semibold text-navy-900">Compliance Summary</h2>
                <button
                  onClick={handleRefreshSummary}
                  className="inline-flex items-center gap-2 rounded-lg border border-teal-600 px-3 py-1.5 text-sm font-medium text-teal-600 hover:bg-teal-50 transition-colors"
                >
                  <ArrowPathIcon className="h-4 w-4" />
                  Refresh Summary
                </button>
              </div>

              {!summary ? (
                <div className="rounded-lg border border-gray-100 bg-white p-12 text-center shadow-sm">
                  <p className="text-sm text-gray-500">
                    No compliance summary available. Click "Refresh Summary" to generate.
                  </p>
                </div>
              ) : (
                <>
                  {/* Overall Status Banner */}
                  <div
                    className={clsx(
                      'rounded-lg border p-4',
                      summary.overallStatus === 'compliant' &&
                        'border-green-200 bg-green-50',
                      summary.overallStatus === 'pending_review' &&
                        'border-amber-200 bg-amber-50',
                      summary.overallStatus === 'has_issues' &&
                        'border-red-200 bg-red-50',
                    )}
                  >
                    <div className="flex items-center gap-3">
                      {summary.overallStatus === 'compliant' ? (
                        <ShieldCheckIcon className="h-6 w-6 text-green-600" />
                      ) : (
                        <ExclamationTriangleIcon
                          className={clsx(
                            'h-6 w-6',
                            summary.overallStatus === 'pending_review'
                              ? 'text-amber-600'
                              : 'text-red-600',
                          )}
                        />
                      )}
                      <div>
                        <p
                          className={clsx(
                            'text-sm font-semibold',
                            summary.overallStatus === 'compliant' && 'text-green-800',
                            summary.overallStatus === 'pending_review' && 'text-amber-800',
                            summary.overallStatus === 'has_issues' && 'text-red-800',
                          )}
                        >
                          {summary.overallStatus === 'compliant' && 'All Clear -- Campaign is Fully Compliant'}
                          {summary.overallStatus === 'pending_review' && 'Pending Review -- Some pieces need attention'}
                          {summary.overallStatus === 'has_issues' && 'Issues Found -- Action required'}
                        </p>
                      </div>
                    </div>
                  </div>

                  {/* Per-Channel Breakdown */}
                  {summary.channels.length > 0 && (
                    <div>
                      <h3 className="mb-3 text-sm font-semibold text-gray-700">
                        Per-Channel Breakdown
                      </h3>
                      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3">
                        {summary.channels.map((ch) => (
                          <div
                            key={ch.channel}
                            className="rounded-lg border border-gray-100 bg-white p-4 shadow-sm"
                          >
                            <div className="flex items-center justify-between">
                              <StatusBadge variant="neutral">
                                {CHANNEL_DISPLAY[ch.channel]}
                              </StatusBadge>
                              <span className="text-xs text-gray-400">
                                {ch.totalPieces} piece{ch.totalPieces !== 1 ? 's' : ''}
                              </span>
                            </div>
                            <div className="mt-3 grid grid-cols-3 gap-2 text-center">
                              <div>
                                <p className="text-lg font-semibold text-green-600 tabular-nums">
                                  {ch.compliant}
                                </p>
                                <p className="text-xs text-gray-500">Compliant</p>
                              </div>
                              <div>
                                <p className="text-lg font-semibold text-amber-500 tabular-nums">
                                  {ch.pending}
                                </p>
                                <p className="text-xs text-gray-500">Pending</p>
                              </div>
                              <div>
                                <p className="text-lg font-semibold text-red-600 tabular-nums">
                                  {ch.issues}
                                </p>
                                <p className="text-xs text-gray-500">Issues</p>
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Unresolved Issues */}
                  {summary.unresolvedIssues.length > 0 && (
                    <div>
                      <h3 className="mb-3 text-sm font-semibold text-gray-700">
                        Unresolved Issues ({summary.unresolvedIssues.length})
                      </h3>
                      <div className="overflow-hidden rounded-lg border border-gray-100 bg-white shadow-sm">
                        <ul className="divide-y divide-gray-100">
                          {summary.unresolvedIssues.map((issue) => (
                            <li
                              key={issue.pieceId}
                              className="flex items-center justify-between px-4 py-3"
                            >
                              <div className="flex items-center gap-3">
                                <StatusBadge variant="neutral">
                                  {CHANNEL_DISPLAY[issue.channel]}
                                </StatusBadge>
                                <span className="text-sm font-medium text-gray-900">
                                  {issue.pieceTitle}
                                </span>
                              </div>
                              {issue.status && (
                                <StatusBadge
                                  variant={PIECE_COMPLIANCE_VARIANT[issue.status] || 'neutral'}
                                >
                                  {PIECE_COMPLIANCE_LABEL[issue.status] || String(issue.status)}
                                </StatusBadge>
                              )}
                            </li>
                          ))}
                        </ul>
                      </div>
                    </div>
                  )}
                </>
              )}
            </div>
          </Tab.Panel>
        </Tab.Panels>
      </Tab.Group>

      {/* ── Add Piece Modal ───────────────────────────────────────────────── */}
      <Transition appear show={showAddPieceModal} as={Fragment}>
        <Dialog as="div" className="relative z-50" onClose={() => setShowAddPieceModal(false)}>
          <Transition.Child
            as={Fragment}
            enter="ease-out duration-300"
            enterFrom="opacity-0"
            enterTo="opacity-100"
            leave="ease-in duration-200"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <div className="fixed inset-0 bg-black/30" />
          </Transition.Child>

          <div className="fixed inset-0 overflow-y-auto">
            <div className="flex min-h-full items-center justify-center p-4">
              <Transition.Child
                as={Fragment}
                enter="ease-out duration-300"
                enterFrom="opacity-0 scale-95"
                enterTo="opacity-100 scale-100"
                leave="ease-in duration-200"
                leaveFrom="opacity-100 scale-100"
                leaveTo="opacity-0 scale-95"
              >
                <Dialog.Panel className="w-full max-w-lg rounded-2xl bg-white p-6 shadow-xl">
                  <Dialog.Title className="text-lg font-semibold text-navy-900">
                    Add Content Piece
                  </Dialog.Title>

                  <div className="mt-4 space-y-4">
                    {/* Channel (Listbox) */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700">
                        Channel <span className="text-red-500">*</span>
                      </label>
                      <Listbox
                        value={pieceForm.channel}
                        onChange={(value) => setPieceForm({ ...pieceForm, channel: value })}
                      >
                        <div className="relative mt-1">
                          <Listbox.Button className="relative w-full cursor-pointer rounded-lg border border-gray-300 bg-white py-2 pl-3 pr-10 text-left text-sm focus:outline-none focus:ring-2 focus:ring-teal-500">
                            <span className="block truncate">
                              {CHANNEL_DISPLAY[pieceForm.channel]}
                            </span>
                            <span className="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-2">
                              <ChevronUpDownIcon className="h-5 w-5 text-gray-400" />
                            </span>
                          </Listbox.Button>
                          <Transition
                            as={Fragment}
                            leave="transition ease-in duration-100"
                            leaveFrom="opacity-100"
                            leaveTo="opacity-0"
                          >
                            <Listbox.Options className="absolute z-10 mt-1 max-h-60 w-full overflow-auto rounded-lg bg-white py-1 shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none">
                              {ALL_CHANNELS.map((ch) => (
                                <Listbox.Option
                                  key={ch}
                                  value={ch}
                                  className={({ active }) =>
                                    clsx(
                                      'relative cursor-pointer select-none py-2 pl-10 pr-4 text-sm',
                                      active
                                        ? 'bg-teal-50 text-teal-700'
                                        : 'text-gray-900',
                                    )
                                  }
                                >
                                  {({ selected }) => (
                                    <>
                                      <span
                                        className={clsx(
                                          'block truncate',
                                          selected && 'font-medium',
                                        )}
                                      >
                                        {CHANNEL_DISPLAY[ch]}
                                      </span>
                                      {selected && (
                                        <span className="absolute inset-y-0 left-0 flex items-center pl-3 text-teal-600">
                                          <CheckIcon className="h-4 w-4" />
                                        </span>
                                      )}
                                    </>
                                  )}
                                </Listbox.Option>
                              ))}
                            </Listbox.Options>
                          </Transition>
                        </div>
                      </Listbox>
                    </div>

                    {/* Title */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700">
                        Title <span className="text-red-500">*</span>
                      </label>
                      <input
                        type="text"
                        value={pieceForm.title}
                        onChange={(e) => setPieceForm({ ...pieceForm, title: e.target.value })}
                        placeholder="e.g., Homepage Banner Ad"
                        className="mt-1 block w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-teal-500 focus:ring-teal-500"
                      />
                    </div>

                    {/* Description */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700">Description</label>
                      <textarea
                        value={pieceForm.description}
                        onChange={(e) =>
                          setPieceForm({ ...pieceForm, description: e.target.value })
                        }
                        rows={2}
                        placeholder="Brief description of this content piece"
                        className="mt-1 block w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-teal-500 focus:ring-teal-500"
                      />
                    </div>

                    {/* Content URL */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700">Content URL</label>
                      <input
                        type="url"
                        value={pieceForm.contentUrl}
                        onChange={(e) =>
                          setPieceForm({ ...pieceForm, contentUrl: e.target.value })
                        }
                        placeholder="https://..."
                        className="mt-1 block w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-teal-500 focus:ring-teal-500"
                      />
                    </div>

                    {/* Review Deadline */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700">
                        Review Deadline
                      </label>
                      <input
                        type="date"
                        value={pieceForm.reviewDeadline}
                        onChange={(e) =>
                          setPieceForm({ ...pieceForm, reviewDeadline: e.target.value })
                        }
                        className="mt-1 block w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-teal-500 focus:ring-teal-500"
                      />
                    </div>
                  </div>

                  {/* Actions */}
                  <div className="mt-6 flex justify-end gap-3">
                    <button
                      onClick={() => {
                        setShowAddPieceModal(false);
                        setPieceForm(DEFAULT_PIECE_FORM);
                      }}
                      className="rounded-lg border border-gray-300 px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 transition-colors"
                    >
                      Cancel
                    </button>
                    <button
                      onClick={handleAddPiece}
                      disabled={!pieceForm.title.trim() || addPieceMutation.isPending}
                      className="rounded-lg bg-teal-600 px-4 py-2 text-sm font-medium text-white hover:bg-teal-500 disabled:opacity-50 transition-colors"
                    >
                      {addPieceMutation.isPending ? 'Adding...' : 'Add Piece'}
                    </button>
                  </div>
                </Dialog.Panel>
              </Transition.Child>
            </div>
          </div>
        </Dialog>
      </Transition>

      {/* ── Link Report Modal ─────────────────────────────────────────────── */}
      <Transition appear show={showLinkReportModal} as={Fragment}>
        <Dialog as="div" className="relative z-50" onClose={() => setShowLinkReportModal(false)}>
          <Transition.Child
            as={Fragment}
            enter="ease-out duration-300"
            enterFrom="opacity-0"
            enterTo="opacity-100"
            leave="ease-in duration-200"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <div className="fixed inset-0 bg-black/30" />
          </Transition.Child>

          <div className="fixed inset-0 overflow-y-auto">
            <div className="flex min-h-full items-center justify-center p-4">
              <Transition.Child
                as={Fragment}
                enter="ease-out duration-300"
                enterFrom="opacity-0 scale-95"
                enterTo="opacity-100 scale-100"
                leave="ease-in duration-200"
                leaveFrom="opacity-100 scale-100"
                leaveTo="opacity-0 scale-95"
              >
                <Dialog.Panel className="w-full max-w-sm rounded-2xl bg-white p-6 shadow-xl">
                  <Dialog.Title className="text-lg font-semibold text-navy-900">
                    Link Compliance Report
                  </Dialog.Title>

                  <div className="mt-4">
                    <label className="block text-sm font-medium text-gray-700">
                      Report ID <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="text"
                      value={linkReportId}
                      onChange={(e) => setLinkReportId(e.target.value)}
                      placeholder="Enter the report ID to link"
                      className="mt-1 block w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-teal-500 focus:ring-teal-500"
                    />
                  </div>

                  <div className="mt-6 flex justify-end gap-3">
                    <button
                      onClick={() => {
                        setShowLinkReportModal(false);
                        setActivePieceId(null);
                        setLinkReportId('');
                      }}
                      className="rounded-lg border border-gray-300 px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 transition-colors"
                    >
                      Cancel
                    </button>
                    <button
                      onClick={handleLinkReport}
                      disabled={!linkReportId.trim() || linkReportMutation.isPending}
                      className="rounded-lg bg-teal-600 px-4 py-2 text-sm font-medium text-white hover:bg-teal-500 disabled:opacity-50 transition-colors"
                    >
                      {linkReportMutation.isPending ? 'Linking...' : 'Link Report'}
                    </button>
                  </div>
                </Dialog.Panel>
              </Transition.Child>
            </div>
          </div>
        </Dialog>
      </Transition>
    </div>
  );
}
